/** The words the audit ledger uses for what a check found — one home for the list and for an
 * entry's own page, so the two cannot describe the same chain state differently (design rule 7).
 * Moved out of `Ledger.tsx` unchanged when an entry got a page of its own. */

import type { ChainCheck } from "../api/ledger";
import type { StatusTone } from "../components/StatusPill";
import { instantTime } from "../lib/format";
import type { Reader } from "../lib/format";

/** The three states `ChainCheckOut.state` has, in a reader's words.
 *
 * **Not a boolean, and not a tick.** `verify_chain` returns intact for an empty list, so a page
 * with nothing on it would otherwise get the most reassuring sentence this product has — which is
 * `any([])` being `False` (F07) in another file. Each is a word as well as a colour, because colour
 * is never the only signal (design rule 8).
 */
export const CHECK: Record<string, { word: string; short: string; tone: StatusTone }> = {
  intact: { word: "The entries on this page add up", short: "Adds up", tone: "ok" },
  broken: { word: "This chain does not add up", short: "Does not add up", tone: "crit" },
  nothing_to_check: {
    word: "Nothing on this page to check",
    short: "Nothing to check",
    tone: "neutral",
  },
};

/** The six anchor states, each with its own sentence — **and none of them collapsed.**
 *
 * `liveness.AnchorHealth` keeps *there was nothing to do* apart from *the thing that was due never
 * happened* (ADR-0023), and a screen that merged any two would undo that. The first version of this
 * panel read `unanchored_entries > 0 ? "n not covered" : "the head is covered"` — and
 * `unanchored_entries` is **zero** for a chain that has never been anchored, because there is no
 * anchor for anything to be waiting behind. So a workspace with nothing committed anywhere reported
 * *"The head is covered."* That is F07's shape exactly: the absence of a fact rendered as the most
 * reassuring sentence available, and it is the reason this is a lookup on the state rather than
 * arithmetic on a count.
 *
 * Today every workspace is `never_anchored`: `run_daily_anchor` has a real store now and still has
 * no caller, because what ADR-0013 actually requires is a scheduler alerting on the **absence** of
 * a successful run, and nothing schedules anything (R-14). The copy must say that and must not
 * imply a job exists.
 */
export const ANCHOR: Record<
  string,
  { head: string; short: string; tone: StatusTone; detail: (check: ChainCheck) => string }
> = {
  never_anchored: {
    short: "Never committed",
    tone: "warn",
    head: "Nothing in this chain has been committed anywhere unalterable",
    detail: () =>
      "No head has ever been written to storage that cannot be rewritten, and nothing schedules one.",
  },
  nothing_to_anchor: {
    short: "Nothing to commit",
    tone: "neutral",
    head: "Nothing recorded, so nothing to commit",
    detail: () => "A workspace with no entries has no head to commit, which is not a missed run.",
  },
  up_to_date: {
    short: "Committed",
    tone: "ok",
    head: "The head is committed",
    detail: (check) => `Committed at #${check.last_anchored_seq ?? "—"}.`,
  },
  pending: {
    short: "Commit due",
    tone: "info",
    head: "The newest entries are not committed yet",
    detail: (check) =>
      `Committed up to #${check.last_anchored_seq ?? "—"}; ${check.unanchored_entries} entries since are waiting. The next run is due rather than late.`,
  },
  overdue: {
    short: "Commit overdue",
    tone: "warn",
    head: "A commit is overdue",
    detail: (check) =>
      `Committed up to #${check.last_anchored_seq ?? "—"}; ${check.unanchored_entries} entries have waited longer than the window.`,
  },
  ahead_of_chain: {
    short: "Past the chain",
    tone: "crit",
    head: "A commit names a sequence this chain no longer reaches",
    detail: (check) =>
      `A head was committed at #${check.last_anchored_seq ?? "—"}, which is past this chain's end. That is worse than a late run.`,
  },
};

/** A payload value, as text. The payload **is** the hash preimage, so nothing here is rounded or
 * grouped: trimming a stored `NUMERIC` scale is right everywhere else in this product (rule 5, and
 * the `4200.0000000000` that reached a commitment panel) and wrong on the one screen whose job is
 * to show what was hashed. A reader re-performing `SHA-256` needs these characters. */
export function asText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value === null || value === undefined) return "—";
  // A list or a nested object. Canonical JSON is what was hashed, so this is the closest a cell
  // gets to it; the whole payload is never reformatted into something prettier.
  return JSON.stringify(value);
}

/** The time of day with its zone.
 *
 * An audit trail without times is weak, and a time printed without a zone is worse than none: the
 * same reason `shortDate` never writes a date in digits alone. With a reader (gate 30) the instant
 * is placed in their zone and the zone said. **Without one** the offset is read from the string
 * rather than assumed - only a zero offset is labelled UTC - and an instant this cannot label is
 * shown as a date alone.
 */
export function atTime(iso: string, reader: Reader | undefined): string {
  if (reader) return instantTime(iso, reader) ?? "";
  const match = /T(\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?(Z|\+00:00)$/.exec(iso);
  return match ? `${match[1]} UTC` : "";
}
